import { type ReactNode, useCallback } from "react";
import SingleTechFilter from "~/Search/components/Filters/components/TechFilter/SingleTechFilter";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { TechFilter } from "~/Search/filterConfigModels";
import { Card } from "../../../../Configurators/Linear/components/Card";
import LinearInfo from "../../../../Configurators/Linear/components/LinearInfo";

interface Props {
  image?: ReactNode;
  items: TechFilter[];
  title: string;
}

export const DesktopTechFilterCategory = (props: Props) => {
  const { items, image } = props;

  const renderChildren = renderItems(items);

  return (
    <div className="scha__sa__tech-filter-category--desktop">
      <h2 className="scha__sa__tech-filter-category__title">{props.title}</h2>
      <div className="scha__sa__tech-filter-category__card-row">
        <Card disableHover={true} className="scha__sa__tech-filter-category__card">
          {renderChildren}
        </Card>

        <div className="scha__sa__tech-filter-category__image">{image}</div>
      </div>
    </div>
  );
};

const ConnectedSingleTechFilter = ({ item }: { item: TechFilter }) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const uid = item.uid;

  const min = useWatch({ control, name: `${uid}.min` });
  const max = useWatch({ control, name: `${uid}.max` });

  const onUpdateValues = useCallback(
    (type: "min" | "max", value: number | undefined) => {
      setValue(`${uid}.${type}`, value, { shouldDirty: true, shouldTouch: true });
    },
    [uid, setValue],
  );

  const assistantText = item.assistantLabel ? t(item.assistantLabel) : "";
  const title = (
    <>
      {item.assistantLabel && <span dangerouslySetInnerHTML={{ __html: assistantText }} />}
      {item.description && <LinearInfo info={item.description} />}
    </>
  );

  return (
    <SingleTechFilter
      key={uid}
      title={title}
      placeholderSuffix={item.unit}
      min={min}
      max={max}
      onUpdateValues={onUpdateValues}
      data-test-id={`${uid}`}
    />
  );
};

export const renderItems = (items: TechFilter[]) => {
  return (
    <>
      {items.map((item) => (
        <ConnectedSingleTechFilter key={item.uid} item={item} />
      ))}
    </>
  );
};
